/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, interval } from 'rxjs';
import { finalize, switchMap, take, takeWhile } from 'rxjs/operators';
import {
  ButtonComponent,
  CardComponent,
  InputComponent,
  MessageHandler,
  SpinnerComponent,
} from 'continity-shared-ui';

import { CompanyProfileDto } from './company-profile.dtos';
import { CompanyUpgradeApi } from './company-upgrade.api';
import { DnsValidationService } from './dns-validation.service';
import { DnsValidationStatus, DnsValidationStatusResponse } from './dns-validation.models';
import { DomainValidators } from './domain-validators';
import { EmailValidatorService } from './public-email.service';

/**
 * Personal → Company account upgrade: domain entry → DNS TXT ownership proof (manual verify or
 * auto-polling) → activation. Plus the DEV shortcut that skips the proof on non-production hosts.
 *
 * SHARED BY BOTH PORTALS (this used to live in the main portal only). It is one security-relevant
 * state machine — domain-vs-email matching, public-provider refusal, token handling, expiry,
 * cancellation — and a second copy in the Phoenix portal would have drifted from the original the
 * first time either side changed. Phoenix needs it because the company account with a VERIFIED
 * domain is the precondition for ordering a backup environment: the site's technical prefix is
 * derived from that domain (`PhoenixSitePrefixFactory`), and the verified domain is what proves
 * the tenant actually controls the organization Phoenix is going to recover.
 *
 * The four app-specific dependencies of the original are inputs/outputs now:
 *   • dev environment detection → {@link allowDevUpgrade}
 *   • "register a new account" (logout)  → {@link registerNewAccount}
 *   • support dialog                     → {@link showSupport} / {@link supportRequested}
 *   • API base URL                       → `COMPANY_API_BASE_URL`
 * Everything else — template, data-cy hooks, i18n keys under `company_settings.*` — is unchanged,
 * so the main portal's Cypress coverage keeps applying.
 */
@Component({
  selector: 'cc-company-upgrade',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    SpinnerComponent,
    CardComponent,
    ButtonComponent,
    InputComponent,
  ],
  template: `
    <!-- Domain Registration Section -->
    @if (validationStep === 'domain') {
      <app-card
        [heading]="'company_settings.upgrade_title' | translate"
        data-cy="upgrade-form"
        data-wizard="company-upgrade-card">
        <!-- Error Messages -->
        @if (errorMessage) {
          <app-card variant="error" [error]="errorMessage" data-cy="upgrade-error-message">
          </app-card>
        }
        <!-- Public email warning -->
        @if (emailValidator.isPublicEmail(userEmail)) {
          <app-card variant="warning"
            [heading]="'company_settings.public_email_warning_title' | translate" data-cy="public-email-warning">
            <p>{{ 'company_settings.public_email_warning_message' | translate: { domain: getEmailDomain() } }}</p>
            <p class="mt-2">{{ 'company_settings.public_email_warning_solution' | translate }}</p>
            <div class="mt-4">
              <app-button variant="primary" size="sm" (click)="onRegisterNewAccount()" data-cy="public-email-register-btn">
                {{ 'company_settings.public_email_register_button' | translate }}
              </app-button>
            </div>
          </app-card>
        }
        <form [formGroup]="upgradeForm">
          <!-- Domain Input -->
          <div [class.opacity-50]="emailValidator.isPublicEmail(userEmail)">
            <app-input [label]="'company_settings.domain' | translate"
              [placeholder]="'e.g ' + getEmailDomain() + ' or somesubdomain.' + getEmailDomain()"
              [required]="true" inputType="text" formControlName="domain"
              data-cy="upgrade-domain-input">
            </app-input>
            <!-- Helper Text -->
            <p class="text-xs text-muted-foreground mt-1">
              {{ 'company_settings.domain_hint' | translate }} {{ getEmailDomain() }} {{ 'company_settings.domain_hint_suffix'
              | translate }}
            </p>
            <!-- Error Text -->
            @if (getDomainValidationErrors()) {
              <p class="text-sm text-red-600 dark:text-red-400 mt-1">
                {{ getDomainValidationErrors() }}
              </p>
            }
            <!-- Validation status -->
            @if (upgradeForm.get('domain')?.dirty && upgradeForm.pending) {
              <p
                class="mt-1 text-sm text-blue-600 dark:text-blue-400">
                {{ 'company_settings.checking_domain' | translate }}
              </p>
            }
          </div>
        </form>
        <!-- Action Buttons -->
        <div class="flex items-center space-x-4 mt-6">
          <app-button variant="primary" [disabled]="isUpgradeButtonDisabled() || emailValidator.isPublicEmail(userEmail)"
            [loading]="isLoading" (click)="onUpgradeClick()" data-cy="upgrade-submit-btn">
            <span data-cy="upgrade-button-text">{{ 'company_settings.upgrade_button' | translate }}</span>
          </app-button>
          <span class="text-sm text-muted-foreground">
            {{ 'company_settings.upgrade_note' | translate }}
          </span>
        </div>
        <!-- Dev Quick Upgrade -->
        @if (allowDevUpgrade) {
          <div class="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
            <div class="flex items-center gap-3">
              <app-button variant="warning" size="sm"
                [loading]="isLoading"
                [disabled]="emailValidator.isPublicEmail(userEmail)"
                (click)="devUpgrade()"
                data-cy="dev-upgrade-btn">
                DEV: Sofort upgraden
              </app-button>
              <span class="text-xs text-amber-700 dark:text-amber-300">
                Skippt DNS-Validierung. Domain: {{ upgradeForm.get('domain')?.value?.trim() || getEmailDomain() }}
              </span>
            </div>
          </div>
        }
        <!-- Support-Link -->
        @if (showSupport) {
          <div class="mt-3 flex justify-end">
            <button
              type="button"
              class="text-sm text-primary hover:underline"
              (click)="supportRequested.emit()">
              {{ 'support.title' | translate }}
            </button>
          </div>
        }
      </app-card>
    }

    <!-- DNS Validation Section -->
    @if (validationStep === 'dns') {
      <app-card variant="info"
        [heading]="'company_settings.dns_validation_title' | translate" data-cy="dns-validation-section">
        <p class="mb-2">{{ 'company_settings.dns_validation_instructions' | translate }}</p>
        <!-- Show which domain is being verified if we have existing validation -->
        @if (existingValidation?.domain || upgradeForm.get('domain')?.value) {
          <div
            class="mb-6 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <p class="text-sm">
              <span class="text-muted-foreground">{{ 'company_settings.verifying_domain' | translate }}:</span>
              <strong class="ml-2 font-mono">{{ existingValidation?.domain || upgradeForm.get('domain')?.value }}</strong>
            </p>
          </div>
        }
        <!-- Verification Steps -->
        <app-card variant="default">
          <h4 class="font-semibold mb-3">{{ 'company_settings.dns_validation_steps' | translate }}</h4>
          <ol class="ml-6 space-y-2 list-decimal text-sm">
            <li>{{ 'company_settings.dns_step_1' | translate }}</li>
            <li>{{ 'company_settings.dns_step_2' | translate }}</li>
            <li>{{ 'company_settings.dns_step_3' | translate }}: <strong>TXT</strong></li>
            <li>{{ 'company_settings.dns_step_4' | translate }}</li>
            <li>{{ 'company_settings.dns_step_5' | translate }}</li>
          </ol>
        </app-card>
        <!-- DNS Record Details -->
        <app-card [heading]="'company_settings.dns_record_details' | translate">
          <div class="space-y-4">
            <!-- Record Type -->
            <div>
              <label class="block text-sm font-medium text-muted-foreground">
                {{ 'company_settings.dns_record_type' | translate }}
              </label>
              <p class="font-mono text-lg font-bold">TXT</p>
            </div>
            <!-- Record Name -->
            <div>
              <label class="block text-sm font-medium text-muted-foreground">
                {{ 'company_settings.dns_record_name' | translate }}
              </label>
              <div class="flex items-center space-x-2">
                <code class="flex-1 p-2 bg-muted rounded text-sm font-mono break-all">
                  {{ getValidationRecordName() }}
                </code>
                <app-button variant="secondary" size="sm" (click)="copyToClipboard(getValidationRecordName())"
                  data-cy="copy-record-name-btn">
                  Copy
                </app-button>
              </div>
              <p class="text-xs text-muted-foreground mt-1">
                {{ 'company_settings.dns_record_name_hint' | translate }}
              </p>
            </div>
            <!-- Record Value -->
            <div>
              <label class="block text-sm font-medium text-muted-foreground">
                {{ 'company_settings.dns_record_value' | translate }}
              </label>
              <div class="flex items-center space-x-2">
                <code class="flex-1 p-2 bg-muted rounded text-sm font-mono break-all">
                  {{ validationToken }}
                </code>
                <app-button variant="secondary" size="sm" (click)="copyToClipboard(validationToken)" data-cy="copy-token-btn">
                  Copy
                </app-button>
              </div>
            </div>
          </div>
        </app-card>
        <!-- Validation Status -->
        <app-card [variant]="getStatusVariant()"
          [heading]="'company_settings.validation_status.' + getValidationStatusKey() | translate">
          <p class="text-sm">
            {{ 'company_settings.validation_status_message.' + getValidationStatusKey() | translate }}
          </p>
          <!-- Tips -->
          <div class="mt-4 text-xs text-muted-foreground space-y-1">
            <p>💡 {{ 'company_settings.dns_tip_1' | translate }}</p>
            <p>⏱️ {{ 'company_settings.dns_tip_2' | translate }}</p>
          </div>
        </app-card>
        <!-- Action Buttons -->
        <div class="flex flex-wrap gap-3 mt-6">
          <app-button variant="primary" [loading]="isLoading && !isValidating"
            (click)="verifyDnsRecord(upgradeForm.get('domain')?.value || existingValidation?.domain)"
            data-cy="verify-dns-btn">
            {{ isLoading && !isValidating ? ('company_settings.verifying' | translate) : ('company_settings.verify_dns' |
            translate) }}
          </app-button>
          <app-button variant="secondary" [loading]="isValidating"
            (click)="startPolling(upgradeForm.get('domain')?.value || existingValidation?.domain)" data-cy="auto-verify-btn">
            @if (!isValidating) {
              <span>
                {{ 'company_settings.auto_verify' | translate }}
              </span>
            }
            @if (isValidating) {
              <span>
                {{ 'company_settings.auto_verifying' | translate }}
                @if (nextPollIn > 0) {
                  <small class="ml-1 text-xs">({{ nextPollIn }}s)</small>
                }
              </span>
            }
          </app-button>
          <!-- Cancel Validation button replaces Back to Domain Entry -->
          <app-button variant="error" (click)="cancelValidation()" data-cy="cancel-validation-btn">
            {{ 'company_settings.cancel_validation' | translate }}
          </app-button>
        </div>
      </app-card>
    }

    <!-- Completion Section -->
    @if (validationStep === 'complete') {
      <app-card variant="success"
        [heading]="'company_settings.dns_validation_success' | translate" data-cy="validation-complete-section">
        <p>{{ 'company_settings.dns_validation_success_message' | translate }}</p>
        <!-- Loading indicator -->
        @if (isLoading) {
          <div class="flex justify-center py-6">
            <app-spinner size="lg"></app-spinner>
            <span class="ml-3 text-sm text-muted-foreground">
              {{ 'company_settings.finalizing_account' | translate }}
            </span>
          </div>
        }
      </app-card>
    }
  `,
})
export class CompanyUpgradeComponent
  extends MessageHandler
  implements OnInit, OnDestroy, OnChanges {
  private fb = inject(FormBuilder);
  private api = inject(CompanyUpgradeApi);
  private translate = inject(TranslateService);
  private domainValidators = inject(DomainValidators);
  private dnsValidationService = inject(DnsValidationService);
  emailValidator = inject(EmailValidatorService);

  @Input() company: CompanyProfileDto | null = null;
  @Input() userEmail = '';
  @Input() existingValidation: DnsValidationStatusResponse | null = null;
  /** DEV shortcut that skips the DNS proof — the host app decides (non-production hosts only). */
  @Input() allowDevUpgrade = false;
  /** The support link is main-portal chrome; off by default. */
  @Input() showSupport = false;

  @Output() upgradeCompleted = new EventEmitter<boolean>();
  @Output() upgradeSuccess = new EventEmitter<string>();
  @Output() upgradeError = new EventEmitter<string>();
  @Output() validationCancelled = new EventEmitter<void>();
  @Output() supportRequested = new EventEmitter<void>();
  /** Public-provider accounts cannot be upgraded — the host app logs out into registration. */
  @Output() registerNewAccount = new EventEmitter<void>();

  upgradeForm!: FormGroup;
  isLoading = false;

  // DNS Validation States
  validationStep: 'domain' | 'dns' | 'complete' = 'domain';
  validationToken: string | null = null;
  validationStatus: DnsValidationStatus = DnsValidationStatus.NotRequired;
  isValidating = false;
  validationPolling?: Subscription;

  nextPollIn = 0;
  private countdownSub?: Subscription;

  ngOnInit(): void {
    // Domain validation with AsyncValidator
    this.upgradeForm = this.fb.nonNullable.group({
      domain: [
        '',
        [Validators.required, Validators.pattern(/^[a-zA-Z0-9.-]+\.[a-z]{2,}$/)],
        // Getter, not value: `userEmail` is an @Input fed from an async user load and is often
        // still empty at ngOnInit — a captured empty string would disable the check for good.
        [this.domainValidators.matchesEmailDomain(() => this.userEmail)],
      ],
    });

    // Disable the domain field if the user has a public email
    if (this.emailValidator.isPublicEmail(this.userEmail)) {
      this.upgradeForm.get('domain')?.disable();
    }

    // Initialize from any existing validation (e.g. resumed pending validation)
    this.initializeFromExistingValidation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingValidation'] && this.upgradeForm) {
      this.initializeFromExistingValidation();
    }
  }

  override ngOnDestroy(): void {
    this.cleanupPolling();
  }

  private initializeFromExistingValidation(): void {
    if (!this.existingValidation) {
      return;
    }

    // Set domain in form
    if (this.existingValidation.domain) {
      this.upgradeForm.patchValue({ domain: this.existingValidation.domain });
      // Disable the domain field if there's an existing validation
      this.upgradeForm.get('domain')?.disable();
    }

    // Set validation details
    this.validationToken = this.existingValidation.validationToken;
    this.validationStatus = this.existingValidation.status;

    // Automatically switch to the correct UI
    if (this.existingValidation.isValidationRequired) {
      if (this.existingValidation.status === DnsValidationStatus.Pending) {
        this.validationStep = 'dns';
        // Start auto-polling for pending validations
        setTimeout(() => this.startPolling(), 100);
      } else if (this.existingValidation.status === DnsValidationStatus.Success) {
        this.validationStep = 'complete';
      }
    }
  }

  getDomainValidationErrors(): string {
    const domainControl = this.upgradeForm.get('domain');
    if (!domainControl?.touched || !domainControl?.invalid) {
      return '';
    }

    if (domainControl.hasError('required')) {
      return this.translate.instant('company_settings.error.domain_required');
    }

    if (domainControl.hasError('pattern')) {
      return this.translate.instant('company_settings.error.domain_invalid');
    }

    if (domainControl.hasError('domainMismatch')) {
      return this.translate.instant('company_settings.error.domain_not_matching_email');
    }

    if (domainControl.hasError('publicEmailDomain')) {
      return this.translate.instant('company_settings.error.public_domain');
    }

    if (domainControl.hasError('userEmailPublic')) {
      return this.translate.instant('company_settings.error.user_email_public');
    }

    return '';
  }

  getStatusVariant(): 'default' | 'success' | 'warning' | 'info' | 'error' {
    switch (this.validationStatus) {
      case DnsValidationStatus.Success:
        return 'success';
      case DnsValidationStatus.Failed:
        return 'error';
      case DnsValidationStatus.Pending:
        return 'info';
      default:
        return 'default';
    }
  }

  private cleanupPolling(): void {
    this.validationPolling?.unsubscribe();
    this.countdownSub?.unsubscribe();
  }

  cancelValidation(): void {
    const domain = this.existingValidation?.domain || this.upgradeForm.get('domain')?.value;

    if (!domain) {
      console.warn('No domain available for cancellation');
      return;
    }

    this.dnsValidationService.cancelValidation(domain).subscribe({
      next: () => {
        this.resetValidationState();
        this.validationCancelled.emit();
      },
      error: (error) => {
        console.error('Error cancelling validation:', error);
        this.flashError('company_settings.error.dns_cancellation_failed');
      },
    });
  }

  private resetValidationState(): void {
    this.validationStep = 'domain';
    this.validationToken = null;
    this.validationStatus = DnsValidationStatus.NotRequired;
    this.cleanupPolling();
  }

  shouldShowCancelButton(): boolean {
    return !!(
      this.existingValidation ||
      (this.validationStep === 'dns' && this.validationToken)
    );
  }

  getValidationStatusKey(): string {
    const statusMap: Record<number | string, string> = {
      [DnsValidationStatus.NotRequired]: 'pending',
      [DnsValidationStatus.Pending]: 'pending',
      [DnsValidationStatus.Success]: 'success',
      [DnsValidationStatus.Failed]: 'failed',
    };
    return statusMap[this.validationStatus] || 'pending';
  }

  getEmailDomain(): string {
    return this.userEmail.split('@')[1] || '';
  }

  /**
   * An account with a public email cannot be upgraded — the user has to register again with a
   * company-domain address. The host app decides what that means (the main portal logs out, which
   * lands on the start page where B2C SignUpSignIn is reachable).
   */
  onRegisterNewAccount(): void {
    this.registerNewAccount.emit();
  }

  onUpgradeClick(): void {
    if (this.isUpgradeButtonDisabled()) return;

    const domain = this.upgradeForm.get('domain')?.value;
    if (!domain) {
      this.flashError('company_settings.error.domain_required');
      return;
    }

    switch (this.validationStep) {
      case 'domain':
        this.checkDomainAndProceed(domain);
        break;
      case 'dns':
        this.verifyDnsRecord(domain);
        break;
      case 'complete':
        this.performUpgrade(domain);
        break;
    }
  }

  isUpgradeButtonDisabled(): boolean {
    if (this.validationStep === 'dns') {
      return this.isLoading;
    }

    const domainControl = this.upgradeForm.get('domain');
    return (
      this.upgradeForm.invalid ||
      this.upgradeForm.pending ||
      this.isLoading ||
      domainControl?.hasError('domainMismatch') === true ||
      domainControl?.hasError('publicEmailDomain') === true ||
      domainControl?.hasError('userEmailPublic') === true
    );
  }

  private checkDomainAndProceed(domain: string): void {
    const companyId = this.company?.id;
    if (!companyId) {
      this.flashError('company_settings.error.unexpected');
      return;
    }

    this.isLoading = true;

    this.api.checkDomainAvailability(companyId, domain).subscribe({
      next: (availabilityResult) => {
        if (!availabilityResult.isAvailable) {
          if (availabilityResult.error) {
            this.handleDomainError(availabilityResult.error);
          } else {
            this.flashError('company_settings.error.domain_invalid');
          }
          return;
        }
        this.checkDnsValidationRequirement(domain);
      },
      error: (error) => {
        this.handleApiError(error);
      },
    });
  }

  private checkDnsValidationRequirement(domain: string): void {
    this.dnsValidationService.getDomainValidationState(domain).subscribe({
      next: (response) => {
        if (!response.isValidationRequired || response.canProceed) {
          this.performUpgrade(domain);
        } else {
          this.validationToken = response.validationToken || null;
          this.validationStatus = DnsValidationStatus.Pending;
          this.validationStep = 'dns';
          this.isLoading = false;
        }
      },
      error: (error) => {
        this.handleApiError(error);
      },
    });
  }

  verifyDnsRecord(domain?: string): void {
    const domainToVerify =
      domain || this.upgradeForm.get('domain')?.value || this.existingValidation?.domain;

    if (!domainToVerify) {
      this.flashError('company_settings.error.domain_required');
      return;
    }

    this.isLoading = true;
    this.isValidating = true;

    this.dnsValidationService.verifyValidation(domainToVerify).subscribe({
      next: (response) => {
        if (response.isExpired) {
          this.validationStatus = DnsValidationStatus.Failed;
          this.isLoading = false;
          this.isValidating = false;
          this.flashError('company_settings.error.validation_expired');
          // Optionally reset to domain step
          setTimeout(() => this.resetValidation(), 3000);
        } else if (response.isValid) {
          this.validationStatus = DnsValidationStatus.Success;
          this.validationStep = 'complete';
          this.performUpgrade(domainToVerify);
        } else {
          this.validationStatus = DnsValidationStatus.Pending;
          this.isLoading = false;
          this.isValidating = false;
          if (response.errorCode === 'NO_VALIDATION_FOUND') {
            this.flashError('company_settings.error.no_validation_found');
          } else {
            this.flashError('company_settings.error.dns_validation_failed');
          }
        }
      },
      error: (error) => {
        this.handleApiError(error);
        this.isValidating = false;
      },
    });
  }

  startPolling(domain?: string): void {
    const domainToPoll =
      domain || this.upgradeForm.get('domain')?.value || this.existingValidation?.domain;

    if (!domainToPoll) {
      this.flashError('company_settings.error.domain_required');
      return;
    }

    this.validationPolling?.unsubscribe();
    this.isValidating = true;
    this.nextPollIn = 10;

    this.countdownSub?.unsubscribe();
    this.countdownSub = interval(1000)
      .pipe(takeWhile(() => this.nextPollIn > 0))
      .subscribe(() => this.nextPollIn--);

    this.validationPolling = interval(10000)
      .pipe(
        take(30),
        switchMap(() => {
          this.nextPollIn = 10;
          return this.dnsValidationService.verifyValidation(domainToPoll);
        }),
        takeWhile((resp) => !resp.isValid && !resp.isExpired, true),
        finalize(() => {
          this.isValidating = false;
          this.countdownSub?.unsubscribe();
        })
      )
      .subscribe({
        next: (resp) => {
          if (resp.isExpired) {
            this.validationPolling?.unsubscribe();
            this.validationStatus = DnsValidationStatus.Failed;
            this.flashError('company_settings.error.validation_expired');
            setTimeout(() => this.resetValidation(), 3000);
          } else if (resp.isValid) {
            this.validationPolling?.unsubscribe();
            this.validationStatus = DnsValidationStatus.Success;
            this.validationStep = 'complete';
            this.flashSuccess('company_settings.dns_validation_success_short');
            this.performUpgrade(domainToPoll);
          } else {
            this.validationStatus = resp.status;
          }
        },
        error: () => {
          this.validationPolling?.unsubscribe();
          this.isValidating = false;
          this.countdownSub?.unsubscribe();
          this.flashError('company_settings.error.dns_validation_failed');
        },
      });
  }

  private performUpgrade(domain: string): void {
    const companyId = this.company?.id;
    if (!companyId) {
      this.flashError('company_settings.error.unexpected');
      return;
    }

    this.isLoading = true;
    this.api.upgradeToCompany(companyId, domain).subscribe({
      next: () => {
        const msg = this.translate.instant('company_settings.upgrade_success');
        this.upgradeSuccess.emit(msg);
        this.upgradeForm.reset();
        this.upgradeCompleted.emit(true);
      },
      error: (err) => this.emitUpgradeFailure(err),
    });
  }

  copyToClipboard(text: string | null): void {
    if (!text) return;

    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.showSuccess(this.translate.instant('company_settings.copied_to_clipboard'));
      })
      .catch(() => {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        this.showSuccess(this.translate.instant('company_settings.copied_to_clipboard'));
      });
  }

  private handleDomainError(errorCode: string): void {
    const errorMap: Record<string, string> = {
      DOMAIN_NOT_MATCHING_EMAIL: 'company_settings.error.domain_not_matching_email',
      ALREADY_COMPANY_ACCOUNT: 'company_settings.error.already_company_account',
      PUBLIC_EMAIL_PROVIDER: 'company_settings.error.user_email_public',
      PUBLIC_DOMAIN_NOT_ALLOWED: 'company_settings.error.public_domain',
      DOMAIN_ALREADY_IN_USE: 'company_settings.error.domain_already_in_use',
    };

    this.flashError(errorMap[errorCode] || 'company_settings.error.domain_invalid');
    this.isLoading = false;
  }

  private handleApiError(e: any): void {
    // ErrorResponse structure: { error: { code: string, details?: string, data?: any } }
    if (e.error?.code) {
      this.handleDomainError(e.error.code);
    } else {
      this.flashError('company_settings.error.unexpected');
    }
    this.isLoading = false;
  }

  devUpgrade(): void {
    const enteredDomain = this.upgradeForm.get('domain')?.value?.trim();
    const domain = enteredDomain || this.getEmailDomain();
    if (!domain) {
      this.flashError('company_settings.error.domain_required');
      return;
    }
    this.isLoading = true;
    this.api.devUpgradeToCompany(domain).subscribe({
      next: () => {
        const msg = this.translate.instant('company_settings.upgrade_success');
        this.upgradeSuccess.emit(msg);
        this.upgradeForm.reset();
        this.upgradeCompleted.emit(true);
      },
      error: (err) => this.emitUpgradeFailure(err),
    });
  }

  /** Shared failure path of both upgrade routes: translate the backend code, tell the host. */
  private emitUpgradeFailure(err: any): void {
    this.isLoading = false;
    const code = err.error?.code;
    const msg =
      code && this.translate.instant(code) !== code
        ? this.translate.instant(code)
        : this.translate.instant('company_settings.error.unexpected');
    this.upgradeError.emit(msg);
    this.upgradeCompleted.emit(false);
  }

  resetValidation(): void {
    this.resetValidationState();
  }

  private flashSuccess(i18nKey: string): void {
    this.successMessage = this.translate.instant(i18nKey);
    this.errorMessage = '';
    this.isLoading = false;
    this.clearMessages();
  }

  private flashError(i18nKeyOrMsg: string): void {
    this.errorMessage =
      i18nKeyOrMsg.startsWith('company_') || i18nKeyOrMsg.startsWith('invitation_')
        ? this.translate.instant(i18nKeyOrMsg)
        : i18nKeyOrMsg;
    this.successMessage = '';
    this.isLoading = false;
    this.clearMessages();
  }

  getValidationRecordName(): string {
    const domain = this.upgradeForm.get('domain')?.value || this.existingValidation?.domain;
    if (!domain) return '';

    return this.dnsValidationService.getValidationRecordName(domain);
  }
}
